import React, { useState, useEffect, useCallback } from 'react';
import { LayoutDashboard, AlertTriangle, AlertOctagon, Ban, FileText } from 'lucide-react';
import { ValidationView, ValidationProject } from '../../types/validation';
import { getProjects, Project as ApiProject } from '../../services/api';
import ControlTower from './ControlTower';
import Stage1View from './Stage1View';
import Stage2View from './Stage2View';
import NonEligibleView from './NonEligibleView';
import SummaryReport from './SummaryReport';
import ProjectDetailModal from './ProjectDetailModal';
import ComparisonModal from './ComparisonModal';

// Convert API Project to ValidationProject
const convertToValidationProject = (apiProject: ApiProject): ValidationProject | null => {
  // Provide default values for newly uploaded projects missing validation fields
  // This ensures all projects appear in Overview pages immediately after upload
  const type = apiProject.type || 'new';
  const category = apiProject.category || 'infrastructure';
  const status = apiProject.status || 'clean';
  const basin = apiProject.basin || 'Unspecified';

  return {
    id: apiProject.id,
    externalId: apiProject.externalId,
    name: apiProject.name,
    type: type as 'new' | 'historic',
    category: category as 'reforestation' | 'wetland' | 'efficiency' | 'infrastructure',
    status: status as 'clean' | 'stage1' | 'stage2' | 'non-eligible',
    stage: apiProject.stage || '-',
    flags: apiProject.flags ? JSON.parse(apiProject.flags) : [],
    details: {
      basin: basin,
      volumetricWaterBenefit: apiProject.volumetricWaterBenefit || '',
      costShare: apiProject.costShare || '',
      quantificationMethod: apiProject.quantificationMethod || '',
      benefitEndDate: apiProject.benefitEndDate || '',
      previousYear: apiProject.previousYearData ? JSON.parse(apiProject.previousYearData) : undefined,
    },
    hasComparison: apiProject.hasComparison || false,
  };
};

const ValidationOverview: React.FC = () => {
  const [activeView, setActiveView] = useState<ValidationView>('controlTower');
  const [selectedProject, setSelectedProject] = useState<ValidationProject | null>(null);
  const [comparisonProject, setComparisonProject] = useState<ValidationProject | null>(null);
  const [projects, setProjects] = useState<ValidationProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const apiProjects = await getProjects();
      const validationProjects = apiProjects
        .map(convertToValidationProject)
        .filter((p): p is ValidationProject => p !== null);
      setProjects(validationProjects);
      setError(null);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load validation projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleProjectUpdate = () => fetchProjects();

  const handleViewProject = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (project) setSelectedProject(project);
  };

  const handleViewComparison = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (project) setComparisonProject(project);
  };

  const navItems: { id: ValidationView; label: string; icon: React.ReactNode }[] = [
    { id: 'controlTower', label: 'Control Tower', icon: <LayoutDashboard className="w-4 h-4" /> },
    { id: 'stage1', label: 'Stage 1 - Flagged', icon: <AlertTriangle className="w-4 h-4" /> },
    { id: 'stage2', label: 'Stage 2 - Flagged', icon: <AlertOctagon className="w-4 h-4" /> },
    { id: 'nonEligible', label: 'Non-Eligible', icon: <Ban className="w-4 h-4" /> },
    { id: 'report', label: 'Summary Report', icon: <FileText className="w-4 h-4" /> },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Sub-navigation */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <nav className="flex space-x-2">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveView(item.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                activeView === item.id
                  ? 'bg-cyan-100 text-cyan-700'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-auto px-4 py-4">
        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Loading validation projects...</div>
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-64">
            <div className="text-red-500">{error}</div>
          </div>
        )}
        {!loading && !error && (
          <>
            {activeView === 'controlTower' && <ControlTower projects={projects} onViewProject={handleViewProject} onViewComparison={handleViewComparison} />}
            {activeView === 'stage1' && <Stage1View projects={projects} onViewProject={handleViewProject} onViewComparison={handleViewComparison} onProjectUpdate={handleProjectUpdate} />}
            {activeView === 'stage2' && <Stage2View projects={projects} onViewProject={handleViewProject} onViewComparison={handleViewComparison} onProjectUpdate={handleProjectUpdate} />}
            {activeView === 'nonEligible' && <NonEligibleView projects={projects} onViewProject={handleViewProject} />}
            {activeView === 'report' && <SummaryReport projects={projects} />}
          </>
        )}
      </div>

      {/* Modals */}
      {selectedProject && <ProjectDetailModal project={selectedProject} onClose={() => setSelectedProject(null)} />}
      {comparisonProject && (
        <ComparisonModal
          project={comparisonProject}
          onClose={() => setComparisonProject(null)}
          onProjectUpdate={handleProjectUpdate}
          startInEditMode={!comparisonProject.flags.some(f => f.type === 'System vs Document Inconsistency')}
        />
      )}
    </div>
  );
};

export default ValidationOverview;
